//@ts-check
import {useProductCombo} from '@/context/ProductCombo/ProductComboContext'
import {useState} from 'react'

export const useProductComboSegments = () => {
  const {
    productCombo: {allCustomers, excludeSegment, segmentConfiguration, comboSkus},
    setProductCombo,
  } = useProductCombo()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [toggleResetKey, setToggleResetKey] = useState(0)
  const [pendingChange, setPendingChange] = useState(
    /** @type {
     *  | {type: 'segmentSubmission', selectedSegments: unknown, selectionType: string}
     *  | {type: 'allCustomersToggle', value: boolean}
     *  | {type: 'excludeToggle', value: boolean}
     *  | {type: 'removeSegment', segmentId: string}
     *  | null
     * } */ (null)
  )
  const includedSegmentGroup = segmentConfiguration?.includedSegments?.[0] ?? null

  const RESET_ON_SEGMENT_CHANGE = {
    comboSkus: [],
    displayCategories: [],
    displayCategoryIds: [],
  }

  /**
   * @param {{segmentConfiguration?: object, [key: string]: unknown}} patch
   */
  const applySegmentChange = patch => {
    const {segmentConfiguration: segmentPatch, ...rootPatch} = patch
    setProductCombo(prev => ({
      ...prev,
      ...RESET_ON_SEGMENT_CHANGE,
      ...rootPatch,
      segmentConfiguration: {
        ...prev.segmentConfiguration,
        ...segmentPatch,
      },
    }))
  }

  const handleSubmitSegments = (selectedSegments, _groupIndex, type) => {
    const group = {selectionType: type, segments: selectedSegments}
    applySegmentChange({
      segmentConfiguration: isExcludeEnabled ? {excludedSegment: group} : {includedSegments: [group]},
    })
  }

  /** @param {string} segmentId */
  const handleRemove = segmentId => {
    if (isExcludeEnabled) {
      const updatedExcludedSegments = segmentConfiguration?.excludedSegment?.segments?.filter(
        segment => segment.segmentId !== segmentId
      )
      applySegmentChange({
        segmentConfiguration: {
          excludedSegment: {
            ...(segmentConfiguration?.excludedSegment ?? {}),
            segments: updatedExcludedSegments,
          },
        },
      })
    } else {
      const updatedIncludedSegments = includedSegmentGroup?.segments?.filter(segment => segment.segmentId !== segmentId)
      applySegmentChange({
        segmentConfiguration: {
          includedSegments: includedSegmentGroup
            ? [{...includedSegmentGroup, segments: updatedIncludedSegments}]
            : null,
        },
      })
    }
  }

  /** @param {boolean} newValue */
  const handleAllCustomersToggle = newValue => {
    applySegmentChange({
      allCustomers: newValue,
      ...(newValue === false ? {excludeSegment: false} : {}),
      segmentConfiguration: {
        includedSegments: null,
        ...(newValue === false ? {excludedSegment: null} : {}),
      },
    })
  }

  /** @param {boolean} newValue */
  const handleExcludeToggle = newValue => {
    applySegmentChange({
      excludeSegment: newValue,
      segmentConfiguration: {excludedSegment: null},
    })
  }

  const isAllCustomers = allCustomers
  const isExcludeEnabled = excludeSegment

  const pendingChangeHandlers = {
    segmentSubmission: change => handleSubmitSegments(change.selectedSegments, null, change.selectionType),
    allCustomersToggle: change => handleAllCustomersToggle(change.value),
    excludeToggle: change => handleExcludeToggle(change.value),
    removeSegment: change => handleRemove(change.segmentId),
  }

  const applyPendingChange = change => {
    if (!change) {
      return
    }
    pendingChangeHandlers[change.type]?.(change)
  }

  const requestOrApplyChange = change => {
    if (comboSkus?.length) {
      setPendingChange(change)
      setShowConfirmationModal(true)
      return
    }

    applyPendingChange(change)
  }

  /** @param {unknown} selectedSegments @param {string} selectionType */
  const handleSegmentSubmissionRequest = (selectedSegments, selectionType) => {
    requestOrApplyChange({type: 'segmentSubmission', selectedSegments, selectionType})
  }

  /** @param {string} segmentId */
  const handleRemoveRequest = segmentId => {
    requestOrApplyChange({type: 'removeSegment', segmentId})
  }

  /** @param {boolean} newValue */
  const handleAllCustomersToggleRequest = newValue => {
    requestOrApplyChange({type: 'allCustomersToggle', value: newValue})
  }

  /** @param {boolean} newValue */
  const handleExcludeToggleRequest = newValue => {
    requestOrApplyChange({type: 'excludeToggle', value: newValue})
  }

  const handleConfirmPendingChange = () => {
    applyPendingChange(pendingChange)

    setShowConfirmationModal(false)
    setPendingChange(null)
  }

  const handleCancelPendingChange = () => {
    // Toggles/removals optimistically flip the (uncontrolled) UI on click, so a
    // remount is needed to snap them back to the source of truth. A segment
    // submission opens a separate modal and never touches these controls.
    if (pendingChange && pendingChange.type !== 'segmentSubmission') {
      setToggleResetKey(prev => prev + 1)
    }

    setShowConfirmationModal(false)
    setPendingChange(null)
  }

  return {
    handleAllCustomersToggle,
    handleExcludeToggle,
    isAllCustomers,
    isExcludeEnabled,
    segmentConfiguration,
    handleSubmitSegments,
    handleRemove,
    handleRemoveRequest,
    toggleResetKey,
    showConfirmationModal,
    handleSegmentSubmissionRequest,
    handleAllCustomersToggleRequest,
    handleExcludeToggleRequest,
    handleConfirmPendingChange,
    handleCancelPendingChange,
  }
}
